'use client';

import { AppImageClient, AppImageData } from '@components/appImage';
import { LazyVideo } from '@components/lazyVideo';
import { ResponsiveSection } from '@components/responsiveSection/ResponsiveSection';
import { Sanitizer } from '@components/sanitizer';

import { PageSeoTitleBlockProps } from './types';
import { CTAButtons } from '../../ctaButtons';
import { Typography } from '../../typography';

interface PageSeoClientTitleBlockProps extends PageSeoTitleBlockProps {
  images: {
    mainLightRound: string;
    mainGrid: string;
  };
}

const renderContent = ({
  className,
  videoSrc,
  videoPoster,
  imageData,
  imageSize,
  imgSrc,
  imgSrcAlt,
}: {
  className: string;
  videoSrc?: string;
  videoPoster?: string;
  imageData?: AppImageData;
  imageSize?: { width: number; height: number };
  imgSrc?: string;
  imgSrcAlt?: string;
}) => {
  if (videoSrc) {
    return (
      <LazyVideo className={className} src={videoSrc} poster={videoPoster} />
    );
  }

  if (imageData && imageSize) {
    return (
      <AppImageClient
        {...imageData}
        width={imageSize.width}
        height={imageSize.height}
        className={className}
        alt={imgSrcAlt ?? 'Заглавное изображение'}
        priority
      />
    );
  }

  return (
    <img
      className={className}
      src={imgSrc}
      alt={imgSrcAlt ?? 'Заглавное изображение'}
    />
  );
};

export const PageSeoClientTitleBlock = ({
  title,
  titleId,
  description,
  descriptionId,
  imgSrc,
  imgSrcAlt,
  imageData,
  imageSize,
  imgSrcSm,
  imgSrcAltSm,
  imageDataSm,
  imageSizeSm,
  mainButton,
  secondaryButton,
  images,
  titleMaxWidth,
  descriptionMaxWidth,
  videoSrc,
  videoPoster,
  renderImage,
  renderImageSm,
}: PageSeoClientTitleBlockProps) => {
  const hasButtons = Boolean(mainButton?.label || secondaryButton?.label);

  return (
    <div className="relative mx-auto flex max-w-[1208px] flex-col justify-center px-5 pt-[40px] max-sm:pt-[104px] sm:px-11 xl:px-5">
      {/** Блок с заголовками */}
      <div className="mx-auto flex flex-col gap-6 pb-10 max-sm:gap-4 max-sm:pb-6">
        <Typography
          id={titleId}
          variant="heading-xl"
          className="flex justify-center max-sm:justify-start"
          style={{ maxWidth: titleMaxWidth }}
        >
          {title}
        </Typography>
        <Typography
          id={descriptionId}
          variant="text-m"
          className="flex justify-center text-center max-sm:justify-start max-sm:text-start"
          style={{ maxWidth: descriptionMaxWidth }}
        >
          {typeof description === 'string' ? (
            <Sanitizer htmlContent={description} as="span" />
          ) : (
            description
          )}
        </Typography>
      </div>

      <ResponsiveSection
        mobile={
          <>
            {renderImageSm ??
              renderContent({
                className: 'bg-surface-base-blur size-full rounded-[10px] p-1',
                videoSrc,
                videoPoster,
                imageData: imageDataSm,
                imageSize: imageSizeSm,
                imgSrc: imgSrcSm,
                imgSrcAlt: imgSrcAltSm,
              })}

            {/** Блок с кнопками */}
            {hasButtons && (
              <div className="flex w-full flex-wrap items-center justify-center gap-4 pt-9">
                <CTAButtons
                  mainButton={mainButton}
                  secondaryButton={secondaryButton}
                />
              </div>
            )}
          </>
        }
        web={
          <>
            {/** Блок с кнопками */}
            {hasButtons && (
              <div className="flex w-full flex-wrap items-center justify-center gap-4 pb-15">
                <CTAButtons
                  mainButton={mainButton}
                  secondaryButton={secondaryButton}
                />
              </div>
            )}

            {renderImage ??
              renderContent({
                className:
                  'bg-surface-base-blur size-full max-w-[1168px] rounded-[32px] p-3 backdrop-blur-[18px]',
                videoSrc,
                videoPoster,
                imageData,
                imageSize,
                imgSrc,
                imgSrcAlt,
              })}
          </>
        }
      />

      {/** Фоновое свечение */}
      <img
        className="absolute -z-1 h-auto w-full opacity-70 max-md:top-40 max-md:scale-120"
        src={images.mainLightRound}
        alt="Фоновое свечение"
      />

      {/** Сетка поверх свечения с прозрачностью */}
      <img
        className="absolute -z-2 h-auto w-full opacity-15 filter max-md:top-2/5 max-md:scale-200"
        src={images.mainGrid}
        alt="Сетка"
      />
    </div>
  );
};
